import React, { useMemo } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import FadeIn from '@components/shared/FadeIn';
import { PageSlice } from '@contracts';
import CarouselChevron from '@images/carousel-chevron.svg';
import useCarousel from '@utils/useCarousel';
import Carousel from './Carousel';
import Colors from './Colors';
import Text from './Text';

interface Props {
  slice: PageSlice;
}

const ContentCarousel: React.FC<Props> = ({ slice }) => {
  const { carouselRef, carouselNext, carouselPrevious, nextDisabled, previousDisabled } = useCarousel();

  return (
    <FadeIn>
      <Container $background={slice.primary.background_color} $textColor={slice.primary.text_color}>
        <Header>
          <Title>{slice.primary.title.text}</Title>
          <Controls>
            <CarouselPrevious onClick={carouselPrevious} disabled={previousDisabled} aria-label="Carousel previous page">
              <CarouselChevron />
            </CarouselPrevious>
            <CarouselNext onClick={carouselNext} disabled={nextDisabled} aria-label="Carousel next page">
              <CarouselChevron />
            </CarouselNext>
          </Controls>
        </Header>
        {useMemo(
          () => (
            <CarouselContainer>
              <Carousel
                ref={carouselRef}
                options={{
                  watchCSS: true,
                  pageDots: true,
                  wrapAround: true,
                  groupCells: false,
                  initialIndex: 1,
                  cellAlign: 'center',
                  lazyLoad: true
                }}
              >
                {slice.items.map((item, idx) => (
                  <CarouselItem key={idx}>
                    <ItemContent>
                      <GatsbyImage
                        image={item.icon.gatsbyImageData}
                        alt={item.icon.alt ?? ''}
                        objectFit="cover"
                        style={{ minWidth: 30 }}
                        className="item-icon"
                        loading="lazy"
                      />
                      <H2>{item.title.text}</H2>
                      <ItemText>
                        <Text richText={item.text.richText} />
                      </ItemText>
                    </ItemContent>
                    <GatsbyImage image={item.image.gatsbyImageData} alt={item.image.alt ?? ''} objectFit="cover" />
                  </CarouselItem>
                ))}
              </Carousel>
            </CarouselContainer>
          ),
          [slice.items]
        )}
      </Container>
    </FadeIn>
  );
};

export default ContentCarousel;

const Container = styled.section<{ $background: string; $textColor: string }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  margin: auto;
  padding: 60px 0 80px;
  overflow: hidden;
  background: ${(p) => p.$background};
  background-size: 100% 188px;
  background-repeat: no-repeat;
  mix-blend-mode: normal;
  color: ${(props) => (props.$textColor ? `${props.$textColor}` : Colors.forestGreen)};

  @media (max-width: 1024px) {
    padding: 50px 0;
    align-items: center;
  }
`;

const Header = styled.header`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto 50px;
  color: ${Colors.navyBlue};

  @media (max-width: 1024px) {
    justify-content: center;
  }
`;

const Title = styled.p`
  font-family: 'BrownStd';
  font-style: normal;
  font-weight: 700;
  font-size: 15px;
  line-height: 140%;
  display: flex;
  align-items: center;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: inherit;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const Controls = styled.div`
  display: flex;
  flex-direction: row;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const CarouselContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;

  .carousel:after {
    content: 'flickity';
    display: none; /* hide :after */
  }

  .flickity-page-dots {
    bottom: -60px;

    @media (max-width: 991px) {
      bottom: -40px;
    }
  }
`;

const CarouselNext = styled.button`
  background: none;
  border: 0;
  height: 22px;
  width: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 30px;
  cursor: pointer;
  padding: 0;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  @media (max-width: 768px) {
    padding: 0 4px;
  }
`;

const CarouselPrevious = styled(CarouselNext)`
  transform: rotate(180deg);
  margin-right: 20px;
`;

const CarouselItem = styled.div`
  width: 786px;
  height: 400px;
  border-radius: 5px;
  display: flex;
  flex-direction: row;
  margin: 0 60px 0 0;

  @media (max-width: 991px) {
    width: 100%;
    height: auto;
    padding: 0 35px;
    flex-direction: column-reverse;

    .item-icon {
      display: none;
    }
  }
`;

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  background: ${Colors.cream};
  gap: 20px;
  padding: 40px;

  @media (max-width: 991px) {
    padding: 20px 30px 30px;
  }
`;

const H2 = styled.h2`
  font-family: 'Domaine';
  font-style: normal;
  font-weight: normal;
  font-size: 30px;
  line-height: 120%;
  color: inherit;
`;

const ItemText = styled.div`
  display: flex;
  flex: 1;
  align-items: flex-end;
`;
